<!-- eslint-disable vue/prop-name-casing -->
<script setup>
import { Validator } from '@vueform/vueform';
import { cloneDeep, uniqBy } from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';
import { onMounted } from 'vue';
import { useModal } from 'vue-final-modal';
import { useAuthStore } from '~/auth/stores/auth.store';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import useEmitter from '~/common/composables/useEmitter';
import { useCommonStore } from '~/common/stores/common.store.js';
import { custom_field_mapping_scenario } from '~/dms/components/transmittals/create-transmittal/transmittal-customfield-mapping-scenario.js';
import { useTransmittalWorkflowPayloadConfiguration } from '~/dms/composables/useTransmittalWorkflow.js';
import { useTransmittalsStore } from '~/dms/store/transmittals.store';

import EmailPopup from '~/forms/components/form-workflows/sidebar/email-block/email-popup.vue';

const props = defineProps({
  published: {
    type: Boolean,
  },
  disabled: {
    type: Boolean,
  },
  is_sub_step: {
    type: Boolean,
  },
  step: {
    type: Object,
  },
  sub_step: {
    type: Object,
  },
  is_final: {
    type: Boolean,
  },
  is_last_step: {
    type: Boolean,
  },
  submit: {
    type: Function,
    default: () => {
      return true;
    },
  },
  existing_members: {
    type: Array,
  },
  allow_assignee_edit: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const auth_store = useAuthStore();
const { $t } = useCommonImports();
const common_store = useCommonStore();
const transmittals_store = useTransmittalsStore();
const { formatSteps } = useTransmittalWorkflowPayloadConfiguration();
const emitter = useEmitter();
const form$ = ref(null);

const custom_emails = Object.assign({}, props.sub_step?.reminders?.config?.map(reminder => reminder?.customEmail) || []);
const can_add_stamp = computed(() => props.is_last_step && (props.step ? props.step.sub_steps.length === 1 : true));
const should_show_stamp = computed(() => {
  // Hide stamp option if minimum approval is more than 1
  if (form$.value?.data?.condition === 'OR' && form$.value?.data?.min_approvals > 1) {
    return false;
  }
  return true;
});
const state = reactive({
  is_advanced_options: false,
  escalation_email: props.sub_step?.escalations?.custom_email || null,
  reminders_email: custom_emails,
});

const reminders_email_popup = useModal({
  component: EmailPopup,
  attrs: {
    onClose() {
      reminders_email_popup.close();
    },
  },
});

function openEmailPopup(index) {
  reminders_email_popup.patchOptions({
    attrs: {
      popup_title: $t('Custom Email'),
      is_popup: true,
      has_dynamic_fields: false,
      form: state.reminders_email?.[index],
      onSave(val) {
        state.reminders_email[index] = val;
        reminders_email_popup.close();
      },
    },
  });
  reminders_email_popup.open();
}

const escalations_email_popup = useModal({
  component: EmailPopup,
  attrs: {
    onClose() {
      escalations_email_popup.close();
    },
    onSave(val) {
      state.escalation_email = val;
      escalations_email_popup.close();
    },
  },
});

function openEscalationEmailPopup() {
  escalations_email_popup.patchOptions({
    attrs: {
      popup_title: $t('Custom Email'),
      is_popup: true,
      has_dynamic_fields: false,
      form: state.escalation_email,
    },
  });
  escalations_email_popup.open();
}

const sub_step = computed(() => props.sub_step);
const is_sub_step = computed(() => props.is_sub_step);

const membersCount = class extends Validator {
  check(value) {
    return form$.value?.data?.members?.length >= value;
  }

  get message() {
    return 'The entered value should not be greater than the members count';
  }
};

const existing_members_excluding_current_user = new Set([...(props.existing_members || []), auth_store.logged_in_user_details?.user_id]);

const existing_members = computed(() => {
  return props.published ? Array.from(existing_members_excluding_current_user) : props.existing_members; // Filter out the current user while creating transmittal
});

function onMembersChanged(members) {
  const users = members.filter(member => member.member);
  const teams = members.filter(member => !member.member);
  const team_users = teams.flatMap((team) => {
    const team_users = common_store.get_team(team.uid)?.users || [];
    return team_users.map(user => ({ name: common_store.get_user_or_team_name(user), uid: user, member: true }));
  });
  const all_members = uniqBy([...users, ...team_users], 'uid');
  form$.value.el$('members').update(all_members);
}

const onSubmit = async function (data, form) {
  const sub_step = {};
  sub_step.name = form.data.name;
  sub_step.uid = props.sub_step?.uid || uuidv4();
  sub_step.members = form.data.members.map(user => user.uid || user);
  sub_step.condition = form.data.condition;
  sub_step.min_approvals = form.data.min_approvals ? +form.data.min_approvals : form.data.min_approvals;
  sub_step.reminders = form.data?.reminders?.is_enabled ? form.data.reminders : undefined;
  sub_step.escalations = form.data?.escalations?.is_enabled ? form.requestData.escalations : undefined;
  sub_step.stamp = form.data?.stamp;
  if (sub_step.reminders)
    sub_step.reminders.config.forEach((reminder, index) => reminder.customEmail = state.reminders_email[index]);
  if (sub_step.escalations)
    sub_step.escalations.custom_email = state.escalation_email;

  // If allow_assignee_edit is true, update the transmittal directly
  if (props.allow_assignee_edit && transmittals_store.transmittal) {
    try {
      // Clone the transmittal workflow
      const workflow = cloneDeep(transmittals_store.transmittal.workflow);

      // Find and update the specific sub_step in the workflow
      const steps = Object.values(workflow.steps || {});
      for (const step of steps) {
        const subStepIndex = step.sub_steps?.findIndex(s => s.uid === sub_step.uid);
        if (subStepIndex !== -1) {
          // Update the sub_step
          step.sub_steps[subStepIndex] = { ...step.sub_steps[subStepIndex], ...sub_step };
          break;
        }
      }

      // Format steps back to object format
      const formattedSteps = formatSteps(steps);

      // Update the transmittal
      await transmittals_store.update_transmittal({
        transmittal_uid: transmittals_store.transmittal.uid,
        transmittals: {
          workflow: {
            ...workflow,
            steps: formattedSteps,
          },
          workflow_template: transmittals_store.transmittal.workflow_template,

        },
      });
      props.submit(sub_step, { on_reject: form.data.on_reject, custom_field_mapping: form.data?.custom_field_mapping?.is_enabled ? form.data?.custom_field_mapping : undefined });

      emit('close');
    }
    catch (err) {
      logger.error(err);
    }
  }
  else {
    // Normal flow: call the submit prop function
    props.submit(sub_step, { on_reject: form.data.on_reject, custom_field_mapping: form.data?.custom_field_mapping?.is_enabled ? form.data?.custom_field_mapping : undefined });
    emitter.emit('validate-documents');
  }
};

onMounted(() => {
  setTimeout(() => {
    state.is_advanced_options = props.sub_step?.reminders?.is_enabled || props.sub_step?.escalations?.is_enabled || props.sub_step?.stamp?.is_enabled || props.step?.custom_field_mapping?.is_enabled;
  }, 100);
});
</script>

<template>
  <hawk-modal-container id="transmittal-field-mapping" content_class="rounded-lg !w-[800px]">
    <Vueform
      ref="form$"
      :default="{ ...sub_step, custom_field_mapping: props.step?.custom_field_mapping }"
      :presets="['']"
      size="sm"
      :display-errors="false"
      :endpoint="onSubmit"
      :columns="{
        default: { container: 12, label: 4, wrapper: 12 },
        sm: { container: 12, label: 4, wrapper: 12 },
        md: { container: 12, wrapper: 12, label: 4 },
      }"
      @mounted="props.sub_step ? form$.load({ ...props.sub_step, custom_field_mapping: props.step?.custom_field_mapping }, true) : null"
    >
      <div class="col-span-12">
        <hawk-modal-header @close="emit('close')">
          <template #title>
            <span v-if="sub_step"> {{ $t('Edit') }} </span>
            <span v-else-if="is_sub_step"> {{ $t('Add sub step') }} </span>
            <span v-else> {{ $t('Add step') }} </span>
          </template>
        </hawk-modal-header>
        <hawk-modal-content :class="{ 'pointer-events-none': disabled && !allow_assignee_edit }">
          <TextElement name="name" :disabled="published || (disabled && !allow_assignee_edit)" :label="$t('Name')" />
          <RadiogroupElement
            class="mt-4"
            :label="$t('Condition')"
            rules="required"
            :disabled="published || (disabled && !allow_assignee_edit) || form$?.data?.stamp?.is_enabled"
            name="condition"
            :items="[
              { label: $t('Require all members to approve'), value: 'AND' },
              { label: $t('Require any members to approve'), value: 'OR' },
            ]"
          />
          <hawk-assignee-input
            class="mt-4"
            :multi="true"
            :format_load="true"
            :options="{
              label: $t('Members'),
              name: 'members',
              appendTo: '#transmittal-field-mapping',
              has_teams: true,
              existing_users: existing_members,
              disabled: (disabled && !allow_assignee_edit) || form$?.data?.stamp?.is_enabled,
              rules: 'required',
              placeholder: $t('Add members and teams'),
            }"
            @change="onMembersChanged"
          />
          <TextElement
            name="min_approvals" :label="$t('Minimum Approval')" input-type="number" class="mt-4" :default="1"
            :conditions="[
              ['condition', '==', 'OR'],
            ]"
            :rules="['required', 'numeric', 'min:1', membersCount]"
            :disabled="published || (disabled && !allow_assignee_edit)"
          />
          <div v-if="allow_assignee_edit" class="text-xs font-bold text-gray-600 mt-2  mb-4">
            {{ $t('Changes to reviewers will not apply to blocks already completed in the current workflow cycle') }}
          </div>
          <RadiogroupElement
            v-if="!(sub_step || is_sub_step)"
            class="mt-4"
            rules="required"
            :label="$t('If rejected')"
            name="on_reject"
            :disabled="published || (disabled && !allow_assignee_edit)"
            :items="[
              { label: 'Continue to the next block', value: 'continue' },
              { label: 'Back to the initiator', value: 'revert' },
            ]"
          />
          <div v-if="!allow_assignee_edit" class="mt-8 cursor-pointer w-40" @click="state.is_advanced_options = !state.is_advanced_options">
            <div class="flex items-center text-primary-700 text-sm font-semibold">
              <IconHawkChevronRight v-if="!state.is_advanced_options" class="w-6 h-6" />
              <IconHawkChevronDown v-else class="w-6 h-6" />
              {{ $t('Advanced options') }}
            </div>
          </div>
          <div v-if="state.is_advanced_options && !allow_assignee_edit">
            <ObjectElement id="escalations" name="escalations" :disabled="disabled">
              <div class="flex gap-4 my-4 col-span-12">
                <div class="flex-1">
                  <div class="text-sm font-semibold">
                    {{ $t("Escalate") }}
                  </div>
                  <div class="text-xs w-120">
                    {{
                      $t(
                        "Escalate to other members if there is no outcome within in the defined time period",
                      )
                    }}.
                  </div>
                </div>
                <ToggleElement
                  name="is_enabled"
                  :disabled="disabled"
                />
              </div>
              <div v-if="form$?.data?.escalations?.is_enabled" class="col-span-12 mb-4">
                <RadiogroupElement
                  :disabled="disabled"
                  name="type"
                  default="add"
                  :items="[
                    { value: 'add', label: $t('Add members') },
                    {
                      value: 'replace',
                      label: $t('Replace members'),
                    },
                  ]"
                  :override-classes="{
                    RadiogroupElement: {
                      wrapper: 'three_columns',
                    },
                  }"
                />
                <hawk-assignee-input
                  class="mt-4"
                  :multi="true"
                  :format_load="true"
                  :options="{
                    label: $t('Choose members'),
                    name: 'members_list',
                    description: $t('The members selected will be added as reviewers of this step.'),
                    disabled,
                    rules: 'required',
                    formatData: (k, v) => ({ [k]: v.map(user => user.uid || user) }),
                    placeholder: $t('Choose members'),
                  }"
                />
                <ObjectElement name="config" class="mt-2">
                  <TextElement
                    name="days"
                    :disabled="disabled"
                    input-type="number"
                    :label="$t('Escalate')"
                    :default=" 1"
                    :add-classes="{ ElementLayout: { innerContainer: '!flex items-center', innerWrapper: 'w-24' } }"
                    :rules="['required', 'integer', 'min:1']"
                    autocomplete="off"
                  >
                    <template #after>
                      <span class="ml-2">
                        {{ $t('after') }} {{ $t('due date') }}
                      </span>
                    </template>
                    <template #addon-after>
                      {{ $t('days') }}
                    </template>
                  </TextElement>
                </ObjectElement>
                <StaticElement :label="$t('Custom Email')" class="mt-6">
                  <HawkButton class="mr-4 -mt-4" type="link" @click="openEscalationEmailPopup">
                    {{ state.escalation_email?.subject ? $t('Edit') : $t('Add') }}
                  </HawkButton>
                </StaticElement>
              </div>
            </ObjectElement>
            <div class="border-b" />
            <ObjectElement name="reminders" :disabled="disabled">
              <div class="flex gap-4 my-4 col-span-12">
                <div class="flex-1">
                  <div class="text-sm font-semibold">
                    {{ $t("Reminders") }}
                  </div>
                  <div class="text-xs w-120">
                    {{
                      $t(
                        "Configure reminders to send periodic emails to pending with reviewers to take an action",
                      )
                    }}.
                  </div>
                </div>
                <ToggleElement
                  :disabled="disabled"
                  name="is_enabled"
                />
              </div>
              <div v-if="form$?.data?.reminders?.is_enabled" class="col-span-12">
                <ListElement
                  name="config"
                  :disabled="disabled"
                  :add-text="`+ ${$t('Add reminder')}`"
                  :presets="['repeatable_list']"
                  rules="required"
                  min="1"
                  :override-classes="{
                    ListElement: {
                      listItem: 'flex w-full bg-gray-50 p-4',
                      remove: ['h-5 w-5 ml-auto'],
                    },
                  }"
                >
                  <template #default="{ index }">
                    <ObjectElement :embed="true" :name="index">
                      <RadiogroupElement
                        name="is_once"
                        :disabled="disabled"
                        :label="$t('Frequency')"
                        class="mb-2"
                        :default="false"
                        :remove-class="{
                          wrapper: 'flex-col',
                        }"
                        :add-classes="{
                          RadiogroupRadio: { container: 'mr-4' },
                        }"
                        :items="[{
                                   value: true,
                                   label: $t('Once'),
                                 },
                                 {
                                   value: false,
                                   label: $t('Multiple times'),
                                 }]"
                      />
                      <FormWorkflowBlockReminders :form-data="{ reminders: form$?.data?.reminders?.config?.[index] }" :payload-data="{}" />
                      <StaticElement :label="$t('Custom Email')" class="mt-6">
                        <HawkButton class="mr-4 -mt-4" type="link" @click="openEmailPopup(index)">
                          {{ state.reminders_email?.[index]?.subject ? $t('Edit') : $t('Add') }}
                        </HawkButton>
                      </StaticElement>
                    </ObjectElement>
                  </template>
                </ListElement>
              </div>
            </ObjectElement>
            <div class="border-b" />
            <transmittal-field-mapping v-if="!allow_assignee_edit" :disabled="disabled" :scenarios="custom_field_mapping_scenario.workflow" :custom-field-mapping="form$?.data?.custom_field_mapping" />
            <ObjectElement v-if="can_add_stamp && !allow_assignee_edit && should_show_stamp" name="stamp" :disabled="disabled" class="border-t">
              <div class="flex gap-4 my-4 col-span-12">
                <div class="flex-1">
                  <div class="text-sm font-semibold">
                    {{ $t("Stamp") }}
                  </div>
                  <div class="text-xs w-120">
                    {{
                      $t(
                        "Require stamp on the documents before document sync.",
                      )
                    }}
                  </div>
                </div>
                <ToggleElement
                  :disabled="disabled || (form$?.data?.condition === 'AND' && form$?.data?.members?.length !== 1) || (form$?.data?.condition === 'OR' && form$?.data?.min_approvals !== 1)"
                  name="is_enabled"
                />
              </div>
            </ObjectElement>
          </div>
        </hawk-modal-content>
        <hawk-modal-footer v-if="!disabled || allow_assignee_edit">
          <template #right>
            <div class="col-span-12">
              <div class="flex justify-end w-full">
                <HawkButton type="outlined" text="Cancel" class="mr-4" @click="emit('close')">
                  {{ $t('Cancel') }}
                </HawkButton>
                <ButtonElement submits size="sm" name="submit" :button-label="$t('Save')" button-class="vf-btn-primary" />
              </div>
            </div>
          </template>
        </hawk-modal-footer>
      </div>
    </Vueform>
  </hawk-modal-container>
</template>
